//--------------- interfaces --------------------
/**
 * Props for the Modal component.
 */
export interface ModalProps {
   /**
    * Additional CSS styles for the main container of the modal.
    */
   mainStyles?: React.CSSProperties;

   /**
    * Additional CSS styles for the top bar of the modal.
    */
   topBarStyles?: React.CSSProperties;

   /**
    * Additional CSS styles for the cancel button in the top bar.
    */
   onCancelButtonStyle?: React.CSSProperties;

   /**
    * Additional CSS styles for the body of the modal.
    */
   bodyStyles?: React.CSSProperties;

   /**
    * Additional CSS styles for the footer of the modal.
    */
   footerStyles?: React.CSSProperties;

   /**
    * The content to be rendered inside the modal.
    */
   children?: React.ReactNode;

   /**
    * The content to be rendered in the top bar of the modal.
    */
   topBarContent?: React.ReactNode;

   /**
    * The content to be rendered in the footer of the modal.
    */
   footerContent?: React.ReactNode;

   /**
    * Callback function invoked when the modal is canceled.
    * @param isOpen - Indicates whether the modal is currently open.
    */
   onCancel?: (isOpen: boolean) => void;

   /**
    * Callback function invoked when the modal is confirmed or OK button is clicked.
    * @param isOpen - Indicates whether the modal is currently open.
    */
   onOk?: (isOpen: boolean) => void;

   /**
    * Indicates whether a background shadow should be displayed behind the modal.
    */
   backgroundShadow?: boolean;

   /**
    * The width of the modal.
    */
   width?: number;

   /**
    * The height of the modal.
    */
   height?: number;

   /**
    * Additional CSS class name(s) to be applied to the modal wrapper.
    */
   wrapClassName?: string;

   /**
    * Indicates whether the modal is currently open or closed.
    */
   isOpen: boolean;

   /**
    * Custom close icon to be rendered in the modal.
    */
   customCloseIcon?: React.ReactNode;

   /**
    * Indicates whether the position of the modal should be calculated dynamically.
    */
   calculatePosition?: boolean;
}

/**
 * Adjusts the position of a modal to ensure it remains within the viewport.
 *
 * @param {number} posX - The current X position of the modal.
 * @param {number} posY - The current Y position of the modal.
 * @param {number} [width=500] - The width of the modal. Default is 500.
 * @param {number} [height=300] - The height of the modal. Default is 300.
 * @returns {{ adjustedX: number, adjustedY: number }} - The adjusted X and Y positions of the modal.
 */
export const adjustModalPosition = (
   posX: number,
   posY: number,
   width: number = 500,
   height: number = 300
) => {
   const screenWidth = window.innerWidth;
   const screenHeight = window.innerHeight;

   const adjustedX = Math.min(Math.max(0, posX), screenWidth - width);
   const adjustedY = Math.min(Math.max(0, posY), screenHeight - height);

   return { adjustedX, adjustedY };
};
